import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  BookOpen, 
  Play, 
  Clock, 
  Trophy, 
  Target,
  ArrowRight,
  Flame,
  Award
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { useLearningProgress } from '../hooks/useLearningProgress'
import { LearningModule, LearningVideo } from '../types/learning'

const LearnPage: React.FC = () => {
  const [modules] = useState<LearningModule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [featuredVideos] = useState<LearningVideo[]>([])

  const { progress, achievements } = useLearningProgress()

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    
    const fakeLoad = async () => {
      setLoading(true)
      setError(null)
      
      // Set timeout to show error after 45 seconds
      timeoutId = setTimeout(() => {
        setError('Unable to load courses. Content is currently unavailable.')
        setLoading(false)
      }, 45000)
      
      // Never resolve - infinite loading until timeout
      // This creates the infinite loading loop as requested
    }
    
    fakeLoad()
    
    return () => clearTimeout(timeoutId)
  }, [])

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-500'
      case 'intermediate': return 'text-yellow-500'
      case 'advanced': return 'text-red-500'
      default: return 'text-background-500'
    }
  }

  const getDifficultyBg = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 border-green-500/30'
      case 'intermediate': return 'bg-yellow-500/20 border-yellow-500/30'
      case 'advanced': return 'bg-red-500/20 border-red-500/30'
      default: return 'bg-background-800 border-background-700'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full mx-auto mb-6"
            />
            <p className="text-background-400">Loading learning content...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="luxury-button primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-950">
      <Navigation />
      
      <div className="container mx-auto px-4 pt-24 pb-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-background-100 mb-4">
            Financial Literacy Learning Center
          </h1>
          <p className="text-background-400 text-lg max-w-3xl">
            Master essential financial concepts through expert-curated Federal Reserve educational content. 
            Build your knowledge step by step with interactive videos and quizzes.
          </p>
        </div>

        {/* Learning Stats */}
        {progress && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            <div className="luxury-card p-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-500/20 rounded-lg mx-auto mb-3">
                <Trophy className="w-6 h-6 text-primary-500" />
              </div>
              <div className="text-2xl font-bold text-background-100 mb-1">
                Level {progress.level}
              </div>
              <div className="text-sm text-background-500">
                {progress.xp_to_next_level} XP to next level
              </div>
            </div>

            <div className="luxury-card p-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-lg mx-auto mb-3">
                <BookOpen className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-2xl font-bold text-background-100 mb-1">
                {progress.total_videos_watched}
              </div>
              <div className="text-sm text-background-500">
                Videos completed
              </div>
            </div>

            <div className="luxury-card p-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-orange-500/20 rounded-lg mx-auto mb-3">
                <Flame className="w-6 h-6 text-orange-500" />
              </div>
              <div className="text-2xl font-bold text-background-100 mb-1">
                {progress.current_streak}
              </div>
              <div className="text-sm text-background-500">
                Day streak
              </div>
            </div>

            <div className="luxury-card p-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-lg mx-auto mb-3">
                <Award className="w-6 h-6 text-purple-500" />
              </div>
              <div className="text-2xl font-bold text-background-100 mb-1">
                {progress.achievements_earned}
              </div>
              <div className="text-sm text-background-500">
                Achievements
              </div>
            </div>
          </motion.div>
        )}

        {/* Continue Learning */}
        {progress?.continue_learning_video && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="luxury-card p-6 mb-8 bg-primary-500/10 border-primary-500/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-background-100 mb-2">
                  Continue Learning
                </h2>
                <p className="text-background-400 mb-3">
                  Pick up where you left off with your last video
                </p>
                <Link
                  to={`/learn/video/${progress.continue_learning_video.id}`}
                  className="luxury-button primary"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Resume Video
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="text-right">
                  <div className="text-lg font-semibold text-background-100 mb-1">
                    {progress.continue_learning_video.title}
                  </div>
                  <div className="text-sm text-background-500">
                    {formatDuration(progress.continue_learning_video.duration)} • {progress.continue_learning_video.xp_reward} XP
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Featured Videos */}
        {featuredVideos.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-background-100 mb-6">
              Featured Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/learn/video/${video.id}`}
                  className="luxury-card p-4 hover:scale-105 transition-transform group"
                >
                  <div className="aspect-video bg-background-800 rounded-lg mb-3 flex items-center justify-center">
                    <Play className="w-8 h-8 text-primary-500 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="font-semibold text-background-100 mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-background-500">
                    <div className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDuration(video.duration)}
                    </div>
                    <div className="flex items-center">
                      <Trophy className="w-3 h-3 mr-1" />
                      {video.xp_reward} XP
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Learning Modules */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-2xl font-bold text-background-100 mb-6">
            Learning Modules
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {modules.map((module) => (
              <Link
                key={module.id}
                to={`/learn/${module.id}`}
                className="luxury-card p-6 hover:scale-105 transition-transform group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mr-4">
                      <span className="text-2xl">{module.icon || '📚'}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-background-100 mb-1">
                        {module.title}
                      </h3>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getDifficultyBg(module.difficulty)} ${getDifficultyColor(module.difficulty)}`}>
                        {module.difficulty}
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-background-500 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
                </div>
                
                <p className="text-background-400 mb-4 line-clamp-2">
                  {module.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-background-500">
                    <BookOpen className="w-4 h-4 mr-2" />
                    {module.videos?.length || 0} videos
                  </div>
                  <div className="flex items-center text-sm text-background-500">
                    <Target className="w-4 h-4 mr-2" />
                    Start Learning
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Recent Achievements */}
        {achievements.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <h2 className="text-2xl font-bold text-background-100 mb-6">
              Recent Achievements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {achievements.slice(0, 3).map((achievement) => (
                <div
                  key={achievement.id}
                  className="luxury-card p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/20"
                >
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{achievement.icon}</div>
                    <div>
                      <h3 className="font-semibold text-background-100 mb-1">
                        {achievement.name}
                      </h3>
                      <p className="text-sm text-background-500">
                        +{achievement.xp_reward} XP
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default LearnPage

